import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import axios from "axios";
import { Check, ArrowRight, FloppyDisk, CreditCard, Money } from "@phosphor-icons/react";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { format } from "date-fns";
import { fr } from "date-fns/locale";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const VEHICLE_NAMES = { classe_e: "Mercedes Classe E", classe_v: "Mercedes Classe V", classe_s: "Mercedes Classe S" };

const DraftEditPage = () => {
  const { draftId } = useParams();
  const navigate = useNavigate();
  const [draft, setDraft] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [notFound, setNotFound] = useState(false);
  const [confirmed, setConfirmed] = useState(false);
  const [date, setDate] = useState(undefined);
  const [form, setForm] = useState({});

  useEffect(() => {
    axios.get(`${API}/booking/draft/${draftId}`).then(({ data }) => {
      setDraft(data);
      setForm(data);
      if (data.pickup_date) {
        try { setDate(new Date(data.pickup_date + "T00:00:00")); } catch (err) { console.error("Date parse error:", err); }
      }
      setLoading(false);
    }).catch(() => { setNotFound(true); setLoading(false); });
  }, [draftId]);

  const handleChange = (key, val) => setForm(p => ({ ...p, [key]: val }));

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = { ...form, pickup_date: date ? format(date, "yyyy-MM-dd") : form.pickup_date };
      const { data } = await axios.put(`${API}/booking/draft/${draftId}`, payload);
      if (data.success) {
        setDraft(data.booking);
        setForm(data.booking);
        toast.success("Brouillon mis à jour");
      }
    } catch (err) { toast.error(err.response?.data?.detail || "Erreur"); }
    finally { setSaving(false); }
  };

  const handleConfirm = async () => {
    setConfirming(true);
    try {
      // Save latest changes first
      const payload = { ...form, pickup_date: date ? format(date, "yyyy-MM-dd") : form.pickup_date };
      await axios.put(`${API}/booking/draft/${draftId}`, payload);
      // Then confirm
      const { data } = await axios.post(`${API}/booking/draft/${draftId}/confirm`);
      if (data.success) {
        if (data.payment_method === "online") {
          const checkoutRes = await axios.post(`${API}/checkout/session`, {
            booking_id: draftId,
            origin_url: window.location.origin,
          });
          if (checkoutRes.data.url) window.location.href = checkoutRes.data.url;
        } else {
          setConfirmed(true);
          toast.success("Réservation confirmée !");
        }
      }
    } catch (err) { toast.error(err.response?.data?.detail || "Erreur — vérifiez tous les champs"); }
    finally { setConfirming(false); }
  };

  const timeSlots = [];
  for (let h = 0; h < 24; h++) for (let m = 0; m < 60; m += 15) timeSlots.push(`${h.toString().padStart(2, "0")}:${m.toString().padStart(2, "0")}`);

  if (loading) return <main className="min-h-screen bg-[#EDEBE4] flex items-center justify-center"><p className="text-[#1A1A1A]/50">Chargement...</p></main>;

  if (notFound) return (
    <main className="min-h-screen bg-[#EDEBE4]">
      <Navigation />
      <div className="max-w-2xl mx-auto px-6 py-24 text-center">
        <h1 className="font-heading text-3xl text-[#1A1A1A] mb-4">Brouillon introuvable</h1>
        <p className="text-[#1A1A1A]/60 mb-8">Ce brouillon a peut-être déjà été finalisé ou le lien est invalide.</p>
        <a href="/services/deplacements" className="btn-dark inline-flex items-center gap-2 px-6 py-3">Nouvelle réservation <ArrowRight size={16} /></a>
      </div>
      <Footer />
    </main>
  );

  if (confirmed) return (
    <main className="min-h-screen bg-[#EDEBE4]">
      <Navigation />
      <div className="max-w-2xl mx-auto px-6 py-24 text-center">
        <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }}>
          <Check size={48} weight="bold" className="text-green-600 mx-auto mb-4" />
          <h1 className="font-heading text-3xl text-[#1A1A1A] mb-4">Réservation confirmée</h1>
          <p className="text-[#1A1A1A]/60 mb-2">Votre transport est confirmé. Vous recevrez un email de confirmation.</p>
          <p className="text-[#1A1A1A] font-heading text-2xl mt-4">{form.total_amount}€</p>
        </motion.div>
      </div>
      <Footer />
    </main>
  );

  return (
    <main className="min-h-screen bg-[#EDEBE4]">
      <Navigation />
      <section className="py-16 sm:py-24">
        <div className="max-w-3xl mx-auto px-6">
          <div className="text-center mb-10">
            <p className="text-xs tracking-[0.2em] uppercase text-[#1A1A1A]/60 mb-3">Modifier votre réservation</p>
            <h1 className="font-heading text-3xl sm:text-4xl text-[#1A1A1A]">Brouillon de réservation</h1>
            <p className="text-[#1A1A1A]/60 text-sm mt-3">Complétez ou modifiez les informations, puis validez votre réservation.</p>
          </div>

          <div className="bg-white border border-[#1A1A1A]/10 p-8">
            <div className="space-y-5">
              {/* Client info */}
              <div className="grid sm:grid-cols-3 gap-4">
                <div><label className="text-[#1A1A1A]/60 text-xs uppercase tracking-wide block mb-2">Nom *</label>
                  <input type="text" value={form.client_name || ""} onChange={(e) => handleChange("client_name", e.target.value)}
                    className="input-luxury w-full bg-transparent" data-testid="draft-client-name" /></div>
                <div><label className="text-[#1A1A1A]/60 text-xs uppercase tracking-wide block mb-2">Téléphone *</label>
                  <input type="tel" value={form.client_phone || ""} onChange={(e) => handleChange("client_phone", e.target.value)}
                    className="input-luxury w-full bg-transparent" data-testid="draft-client-phone" /></div>
                <div><label className="text-[#1A1A1A]/60 text-xs uppercase tracking-wide block mb-2">Email *</label>
                  <input type="email" value={form.client_email || ""} onChange={(e) => handleChange("client_email", e.target.value)}
                    className="input-luxury w-full bg-transparent" data-testid="draft-client-email" /></div>
              </div>

              {/* Vehicle */}
              <div><label className="text-[#1A1A1A]/60 text-xs uppercase tracking-wide block mb-2">Véhicule</label>
                <Select value={form.vehicle_type || "classe_e"} onValueChange={(v) => handleChange("vehicle_type", v)}>
                  <SelectTrigger className="w-full bg-transparent border-b border-[#1A1A1A]/20 rounded-none h-12 text-[#1A1A1A]"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-white border-[#1A1A1A]/10">
                    {Object.entries(VEHICLE_NAMES).map(([id, name]) => <SelectItem key={id} value={id}>{name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>

              {/* Date & Time */}
              <div className="grid sm:grid-cols-2 gap-4">
                <div><label className="text-[#1A1A1A]/60 text-xs uppercase tracking-wide block mb-2">Date *</label>
                  <Popover><PopoverTrigger asChild>
                    <button type="button" className="input-luxury w-full bg-transparent text-left">
                      {date ? format(date, "dd MMMM yyyy", { locale: fr }) : "Sélectionner"}
                    </button></PopoverTrigger>
                    <PopoverContent className="w-auto p-0 bg-white" align="start"><Calendar mode="single" selected={date} onSelect={setDate} disabled={(d) => d < new Date()} initialFocus /></PopoverContent>
                  </Popover>
                </div>
                <div><label className="text-[#1A1A1A]/60 text-xs uppercase tracking-wide block mb-2">Heure *</label>
                  <Select value={form.pickup_time || "09:00"} onValueChange={(v) => handleChange("pickup_time", v)}>
                    <SelectTrigger className="w-full bg-transparent border-b border-[#1A1A1A]/20 rounded-none h-12 text-[#1A1A1A]"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-white max-h-60">{timeSlots.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>

              {/* Addresses */}
              <div><label className="text-[#1A1A1A]/60 text-xs uppercase tracking-wide block mb-2">Adresse de prise en charge *</label>
                <input type="text" value={form.pickup_address || ""} onChange={(e) => handleChange("pickup_address", e.target.value)}
                  className="input-luxury w-full bg-transparent" data-testid="draft-pickup" /></div>
              <div><label className="text-[#1A1A1A]/60 text-xs uppercase tracking-wide block mb-2">Destination</label>
                <input type="text" value={form.dropoff_address || ""} onChange={(e) => handleChange("dropoff_address", e.target.value)}
                  className="input-luxury w-full bg-transparent" data-testid="draft-dropoff" /></div>

              {/* Flight/Train */}
              {form.flight_train_number !== undefined && (
                <div><label className="text-[#1A1A1A]/60 text-xs uppercase tracking-wide block mb-2">N° de vol / train</label>
                  <input type="text" value={form.flight_train_number || ""} onChange={(e) => handleChange("flight_train_number", e.target.value)}
                    className="input-luxury w-full bg-transparent" /></div>
              )}

              {/* Passengers & Luggage */}
              <div className="grid sm:grid-cols-2 gap-4">
                <div><label className="text-[#1A1A1A]/60 text-xs uppercase tracking-wide block mb-2">Passagers</label>
                  <Select value={(form.passengers || 1).toString()} onValueChange={(v) => handleChange("passengers", parseInt(v))}>
                    <SelectTrigger className="w-full bg-transparent border-b border-[#1A1A1A]/20 rounded-none h-12"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-white">{[1,2,3,4,5,6,7].map(n => <SelectItem key={n} value={n.toString()}>{n}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><label className="text-[#1A1A1A]/60 text-xs uppercase tracking-wide block mb-2">Bagages</label>
                  <Select value={(form.luggage || 0).toString()} onValueChange={(v) => handleChange("luggage", parseInt(v))}>
                    <SelectTrigger className="w-full bg-transparent border-b border-[#1A1A1A]/20 rounded-none h-12"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-white">{[0,1,2,3,4,5,6,7,8].map(n => <SelectItem key={n} value={n.toString()}>{n}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>

              {/* Observations */}
              <div><label className="text-[#1A1A1A]/60 text-xs uppercase tracking-wide block mb-2">Observations</label>
                <textarea value={form.special_requests || ""} onChange={(e) => handleChange("special_requests", e.target.value)}
                  rows={2} className="input-luxury w-full bg-transparent resize-none" /></div>

              {/* Payment method */}
              <div>
                <label className="text-[#1A1A1A]/60 text-xs uppercase tracking-wide block mb-3">Mode de règlement</label>
                <div className="grid sm:grid-cols-2 gap-3">
                  {[
                    { val: "online", Icon: CreditCard, label: "Paiement en ligne", desc: "Paiement sécurisé par carte" },
                    { val: "onboard", Icon: Money, label: "Règlement à bord", desc: "Paiement au chauffeur" },
                  ].map(({ val, Icon, label, desc }) => (
                    <button key={val} type="button" onClick={() => handleChange("payment_method", val)}
                      className={`flex items-center gap-3 p-4 border text-left transition-all ${form.payment_method === val ? "border-[#1A1A1A] bg-[#1A1A1A]/5" : "border-[#1A1A1A]/20 hover:border-[#1A1A1A]/40"}`}>
                      <Icon size={24} weight="light" className="text-[#1A1A1A]" />
                      <div><p className="text-[#1A1A1A] text-sm font-medium">{label}</p><p className="text-[#1A1A1A]/60 text-xs">{desc}</p></div>
                    </button>
                  ))}
                </div>
              </div>

              {/* Total & Actions */}
              <div className="border-t border-[#1A1A1A]/10 pt-6 mt-4">
                {form.total_amount > 0 && (
                  <div className="flex justify-between items-center mb-6">
                    <span className="text-[#1A1A1A]/70">Total</span>
                    <span className="font-heading text-3xl text-[#1A1A1A]">{form.total_amount}€</span>
                  </div>
                )}
                <div className="flex gap-3">
                  <button onClick={handleConfirm} disabled={confirming}
                    className="btn-dark flex-1 flex items-center justify-center gap-3 disabled:opacity-50" data-testid="draft-confirm-btn">
                    {confirming ? "Validation..." : form.payment_method === "online" ? "Valider et payer" : "Confirmer la réservation"}
                    <ArrowRight size={16} weight="bold" />
                  </button>
                  <button onClick={handleSave} disabled={saving}
                    className="px-5 py-3 border border-[#1A1A1A]/20 text-[#1A1A1A]/70 hover:bg-[#EDEBE4] transition-colors text-xs flex items-center gap-2 disabled:opacity-50"
                    data-testid="draft-save-btn">
                    <FloppyDisk size={16} /> {saving ? "..." : "Sauvegarder"}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      <Footer />
    </main>
  );
};

export default DraftEditPage;
